import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Camera, ClipboardList, ScanLine, Search, XCircle } from 'lucide-react';
import { ApiError } from '@hutdrop/api-client';
import { formatCents, type OrderStatus, type QrInvalidDetails } from '@hutdrop/shared';
import { CameraScanner } from '../components/CameraScanner';
import { Alert, Button, Empty, PageHeader, Skeleton } from '../components/ui';
import { scanCode, useOrderCounts, useOrderQueue, useServerNow, type OrderQueue } from '../lib/orders';

const placed = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const QUEUES: { id: OrderQueue; label: string; count?: 'new' | 'in_progress' | 'ready' | 'expiring' }[] = [
  { id: 'new', label: 'New', count: 'new' },
  { id: 'in_progress', label: 'Gathering', count: 'in_progress' },
  { id: 'ready', label: 'Ready', count: 'ready' },
  { id: 'expiring', label: 'Expiring soon', count: 'expiring' },
  { id: 'active', label: 'All open' },
  { id: 'completed', label: 'Picked up' },
  { id: 'expired', label: 'Expired' },
  { id: 'cancelled', label: 'Cancelled' },
];
const OPEN: OrderStatus[] = ['CREATED', 'PENDING', 'READY_FOR_PICKUP'];

export function OrderStatusBadge({ status, label }: { status: OrderStatus; label: string }) {
  return <span className={`badge badge--order-${status.toLowerCase().replaceAll('_', '-')}`}>{label}</span>;
}

/** "3h 12m" until the hold ends; red in the last 2 hours. */
export function HoldLeft({ expiresAt }: { expiresAt: string }) {
  const now = useServerNow(15_000);
  const left = Date.parse(expiresAt) - now;
  if (left <= 0) return <span className="text-danger">Hold ended</span>;
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  return <span className={left < 2 * 3_600_000 ? 'text-danger' : ''}>{h > 0 ? `${h}h ${m}m` : `${m}m`}</span>;
}

export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const queue = (params.get('queue') as OrderQueue | null) ?? 'new';
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const orders = useOrderQueue(queue, q);
  const counts = useOrderCounts();
  const rows = orders.data?.pages.flatMap((p) => p.items) ?? [];

  const go = (next: { queue?: OrderQueue; q?: string }) => {
    const p = new URLSearchParams();
    p.set('queue', next.queue ?? queue);
    const term = next.q ?? q;
    if (term) p.set('q', term);
    setParams(p);
  };

  return (
    <div className="page page--wide">
      <PageHeader
        title="Orders"
        subtitle="Pre-orders are held for 24 hours. Customers pay in store and must show photo ID."
        actions={
          <Link to="/orders/scan" className="btn btn--primary">
            <ScanLine size={16} aria-hidden="true" /> Scan pickup code
          </Link>
        }
      />

      <nav className="tabs" aria-label="Order queues">
        {QUEUES.map((t) => {
          const n = t.count ? counts.data?.[t.count] : undefined;
          return (
            <button key={t.id} type="button" className={`tabs__tab${queue === t.id ? ' active' : ''}`} aria-current={queue === t.id ? 'page' : undefined} onClick={() => go({ queue: t.id })}>
              {t.label}
              {n ? <span className={`tab-count${t.id === 'expiring' ? ' tab-count--warn' : ''}`}>{n}</span> : null}
            </button>
          );
        })}
      </nav>

      <div className="toolbar">
        <form
          role="search"
          className="searchbar__form"
          onSubmit={(e) => {
            e.preventDefault();
            go({ q: draft.trim(), queue: draft.trim() ? 'all' : queue });
          }}
        >
          <Search size={16} className="searchbar__icon" aria-hidden="true" />
          <label htmlFor="order-search" className="sd-visually-hidden">
            Search orders
          </label>
          <input
            id="order-search"
            type="search"
            className="searchbar__input"
            placeholder="Order number, customer name or email…"
            value={draft}
            autoComplete="off"
            onChange={(e) => {
              setDraft(e.target.value);
              if (e.target.value === '' && q) go({ q: '' });
            }}
          />
        </form>
      </div>
      {q ? (
        <p className="muted small">
          Showing all orders matching “{q}”.{' '}
          <button type="button" className="link-btn" onClick={() => { setDraft(''); go({ q: '', queue: 'new' }); }}>
            Clear search
          </button>
        </p>
      ) : null}

      {orders.isPending ? (
        <Skeleton />
      ) : orders.isError ? (
        <Alert>{orders.error.message}</Alert>
      ) : rows.length === 0 ? (
        <Empty icon={<ClipboardList size={28} />} title={q ? 'No orders match that search.' : 'Nothing in this queue.'}>
          {queue === 'new' && !q ? 'New pre-orders show up here automatically.' : null}
        </Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table table--clickable">
              <thead>
                <tr>
                  <th scope="col">Order</th>
                  <th scope="col">Customer</th>
                  <th scope="col" className="num">Items</th>
                  <th scope="col" className="num">Est. total</th>
                  <th scope="col">Status</th>
                  <th scope="col">Hold ends in</th>
                  <th scope="col">Placed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/orders/${o.id}`} className="order-link sd-num">
                        {o.orderNumber}
                      </Link>
                    </td>
                    <td>{o.customerName}</td>
                    <td className="num sd-num">{o.itemCount}</td>
                    <td className="num sd-num">{formatCents(o.estimatedTotalCents)}</td>
                    <td>
                      <OrderStatusBadge status={o.status} label={o.statusLabel} />
                    </td>
                    <td className="nowrap">{OPEN.includes(o.status) ? <HoldLeft expiresAt={o.expiresAt} /> : <span className="muted">—</span>}</td>
                    <td className="nowrap muted">{placed.format(new Date(o.createdAt))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {orders.hasNextPage ? (
            <div className="load-more">
              <Button busy={orders.isFetchingNextPage} onClick={() => void orders.fetchNextPage()}>
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

type ScanProblem = { message: string; details?: QrInvalidDetails };

/**
 * Counter scan screen. A USB/Bluetooth scanner "types" the code into the focused box and presses Enter;
 * the camera button covers phones and tablets. A valid code opens the order; it never completes it.
 */
export function OrderScanPage() {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const [problem, setProblem] = useState<ScanProblem | null>(null);

  // Keep the box focused for the next scan (also after a rejected code, once the input is enabled again).
  useEffect(() => {
    if (!busy) input.current?.focus();
  }, [busy]);

  const submit = useCallback(
    async (token: string) => {
      const value = token.trim();
      if (!value || busy) return;
      setBusy(true);
      setProblem(null);
      try {
        const res = await scanCode(value);
        navigate(`/orders/${res.order.id}?scanned=1`);
      } catch (err) {
        if (err instanceof ApiError) setProblem({ message: err.message, details: err.code === 'QR_INVALID' ? (err.details as QrInvalidDetails) : undefined });
        else setProblem({ message: 'Could not check the code. Try again.' });
        setCode('');
      } finally {
        setBusy(false);
      }
    },
    [busy, navigate],
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit(code);
  }

  return (
    <div className="page">
      <Link to="/orders" className="back-link">
        ← Orders
      </Link>
      <PageHeader title="Scan pickup code" subtitle="Scan the QR code on the customer’s phone. You will still need to check their photo ID." />

      <section className="card scan-card">
        <form onSubmit={onSubmit} className="scan-form">
          <label htmlFor="scan-input" className="fld__label">
            Pickup code
          </label>
          <div className="scan-form__row">
            <input
              id="scan-input"
              ref={input}
              className="fld__input scan-form__input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Scan now, or type the code"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={busy}
            />
            <Button type="submit" busy={busy} disabled={!code.trim()}>
              Check code
            </Button>
          </div>
          <p className="fld__hint">Handheld scanners type into this box and press Enter automatically. Keep the box selected.</p>
        </form>
        <Button variant="secondary" onClick={() => setCamera(true)}>
          <Camera size={16} aria-hidden="true" /> Use camera
        </Button>
      </section>

      {problem ? (
        <section className="scan-result scan-result--bad" role="alert" aria-live="assertive">
          <XCircle size={28} aria-hidden="true" />
          <div>
            <h2>Code not accepted</h2>
            <p>{problem.message}</p>
            {problem.details?.order ? (
              <p>
                <Link to={`/orders/${problem.details.order.id}`} className="link-btn">
                  Open order {problem.details.order.orderNumber}
                </Link>
              </p>
            ) : null}
            <p className="muted small">Do not hand over items for a code that is not accepted. Look the order up by number if the customer has one.</p>
          </div>
        </section>
      ) : null}

      <CameraScanner
        open={camera}
        onClose={() => setCamera(false)}
        onResult={(value) => {
          setCamera(false);
          void submit(value);
        }}
      />
    </div>
  );
}

